import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
var _BaseTake;
function _callSuper(_this, derived, args) {
  function isNativeReflectConstruct() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;
    try {
      return !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {}));
    } catch (e) {
      return false;
    }
  }
  derived = _getPrototypeOf(derived);
  return _possibleConstructorReturn(_this, isNativeReflectConstruct() ? Reflect.construct(derived, args || [], _getPrototypeOf(_this).constructor) : derived.apply(_this, args));
}
/** @jsx jsx */
import { Component } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import partial from 'lodash/partial';
import debounce from 'lodash/debounce';
import { Alert } from '@instructure/ui-alerts';
import { Button } from '@instructure/ui-buttons';
import { Overlay, Mask } from '@instructure/ui-overlays';
import { jsx } from '@instructure/emotion';
import { IN_PROGRESS, liveRegion, SimpleModal, withStyleOverrides } from '@instructure/quiz-common';
import t from '@instructure/quiz-i18n/format-message';
import { ScreenReaderContent } from '@instructure/ui-a11y-content';
import { Card, CustomPropTypes, ensureRCEContentIsLoaded, formatTime, Page, printWithCss, QuizzesSpinner as Spinner, TimeRecordingService, TimerService } from '@instructure/quiz-core';
import { TakeBody } from './TakeBody';
import { TakeFooter } from './TakeFooter';
import { TakeHeader } from './TakeHeader';
import { TakingSidebar } from '@instructure/quiz-taking';
import generateStyle from './styles';
import generateComponentTheme from './theme';

// ================================
// the main component that students
// will use to take the quiz
// ================================

var MIN_REFRESH_DELAY = 1000;
var MAX_REFRESH_DELAY = 15000;
var MAX_SESSION_WAIT = 90000;
var BaseTake = /*#__PURE__*/function (_Component) {
  function BaseTake() {
    var _this2;
    _classCallCheck(this, BaseTake);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this2 = _callSuper(this, BaseTake, [].concat(args));
    _defineProperty(_this2, "timerService", null);
    _defineProperty(_this2, "fetchQuizSessionTimeout", null);
    _defineProperty(_this2, "sessionRefreshDelay", MIN_REFRESH_DELAY);
    _defineProperty(_this2, "autoSubmitWhenLoaded", false);
    _defineProperty(_this2, "sessionRequestTime", null);
    _defineProperty(_this2, "state", {
      isOnline: window.navigator.onLine
    });
    _defineProperty(_this2, "handleNetworkStatus", function () {
      _this2.setState({
        isOnline: window.navigator.onLine
      });
    });
    _defineProperty(_this2, "handleWindowFocus", function () {
      _this2.props.takingActions.reportPageFocusedEvent();
    });
    _defineProperty(_this2, "handleWindowBlur", function () {
      _this2.props.takingActions.reportPageBlurredEvent();
    });
    // Debounce the window focus and blur events to prevent spamming and to ensure that event sending
    // is in sync with responses to questions
    _defineProperty(_this2, "debouncedHandleWindowFocus", debounce(_this2.handleWindowFocus, 2500, {
      leading: false,
      maxWait: 5000,
      trailing: true
    }));
    _defineProperty(_this2, "debouncedHandleWindowBlur", debounce(_this2.handleWindowBlur, 2500, {
      leading: false,
      maxWait: 5000,
      trailing: true
    }));
    _defineProperty(_this2, "fetchQuizSession", function () {
      var _this2$props = _this2.props,
        fetchQuizSession = _this2$props.fetchQuizSession,
        onTakeUnauthorized = _this2$props.onTakeUnauthorized,
        quizSessionId = _this2$props.quizSessionId;
      return fetchQuizSession(quizSessionId, _this2.waitForQuizSessionToBeReady, onTakeUnauthorized);
    });
    _defineProperty(_this2, "notifyAboutRemainingTime", function () {
      _this2.props.getTimeRemaining(_this2.props.quizSessionId, function (_ref) {
        var timeLeftInSeconds = _ref.timeLeftInSeconds;
        var timeString = formatTime(timeLeftInSeconds);
        var screenReaderMessage = t('The remaining time is: {timeleft}', {
          timeleft: timeString
        });
        _this2.props.screenreaderNotification(screenReaderMessage);
      });
    });
    _defineProperty(_this2, "waitForQuizSessionToBeReady", function () {
      var quizSession = _this2.props.quizSession;
      if (quizSession.failedToStart()) {
        _this2.clearFetchQuizSessionTimeout();
        return _this2.props.onTakeFailed();
      }
      var isTimedQuiz = _this2.props.showTimer;
      var hasTimingInfo = quizSession.startAt && quizSession.endAt;
      var isSessionReady = (quizSession.inProgress() || quizSession.submitted()) && (!isTimedQuiz || hasTimingInfo);
      if (isSessionReady) {
        _this2.clearFetchQuizSessionTimeout();
        return _this2.onQuizSessionReady();
      }
      if (performance.now() - _this2.sessionRequestTime >= MAX_SESSION_WAIT) {
        return _this2.makeTakeQuizRequest();
      }
      _this2.fetchQuizSessionTimeout = setTimeout(function () {
        _this2.fetchQuizSession();
      }, _this2.sessionRefreshDelay);
      _this2.sessionRefreshDelay = Math.min(2 * _this2.sessionRefreshDelay, MAX_REFRESH_DELAY);
    });
    _defineProperty(_this2, "autoSubmitQuiz", function () {
      if (_this2.props.resumeDataLoaded) {
        var noop = function noop() {};
        _this2.props.closeSubmitConfirmationModal(_this2.props.quizSessionId);
        _this2.props.submitQuiz(_this2.props.quizSessionId, noop, true);
      } else {
        _this2.autoSubmitWhenLoaded = true;
      }
    });
    _defineProperty(_this2, "focusOnButton", function () {
      _this2.overlayBtn.focus();
    });
    _defineProperty(_this2, "overlayBtnRef", function (node) {
      _this2.overlayBtn = node;
    });
    _defineProperty(_this2, "focusOnModal", function () {
      _this2.modalBtn.focus();
    });
    _defineProperty(_this2, "modalBtnRef", function (node) {
      _this2.modalBtn = node;
    });
    _defineProperty(_this2, "pageChildrenWrapperRef", function (node) {
      _this2.pageChildrenWrapper = node;
    });
    _defineProperty(_this2, "readyToTake", function () {
      var _this2$props2 = _this2.props,
        quizSession = _this2$props2.quizSession,
        resumeDataLoaded = _this2$props2.resumeDataLoaded;
      var sessionItemsLoaded = quizSession.sessionItems && quizSession.sessionItems.first();
      return quizSession.sessionItemsCount === 0 || resumeDataLoaded && sessionItemsLoaded;
    });
    _defineProperty(_this2, "refreshPage", function () {
      window.location.reload();
    });
    return _this2;
  }
  _inherits(BaseTake, _Component);
  return _createClass(BaseTake, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      this.takeQuiz();
      window.addEventListener('online', this.handleNetworkStatus);
      window.addEventListener('offline', this.handleNetworkStatus);
      window.addEventListener('focus', this.debouncedHandleWindowFocus);
      window.addEventListener('blur', this.debouncedHandleWindowBlur);
      this.props.makeStyles();
    }
  }, {
    key: "componentDidUpdate",
    value: function componentDidUpdate(prevProps, prevState) {
      var _this3 = this;
      // Stop spamming time endpoint if the timer isn't running after
      // initial check
      if (this.timerService && !this.props.useTimer && prevProps.useTimer) {
        this.timerService.stop();
      }
      if (this.props.quizSessionId !== prevProps.quizSessionId) {
        this.takeQuiz();
      }
      if (this.props.rceError && this.props.rceError !== prevProps.rceError) {
        this.props.reportRCEEvent(this.props.rceError);
      }
      if (this.props.afterQuizSessionItemsRendered && this.readyToTake()) {
        this.props.afterQuizSessionItemsRendered();
      }
      if (this.pageChildrenWrapper && this.props.currentSessionItemPosition !== prevProps.currentSessionItemPosition) {
        this.pageChildrenWrapper.scrollTop = 0;
      }
      if (this.autoSubmitWhenLoaded && this.props.resumeDataLoaded !== prevProps.resumeDataLoaded) {
        this.autoSubmitQuiz();
      }
      if (this.readyToTake() && this.printOnLoad() && !this.printed) {
        this.printed = true;
        // for some reason, the file upload banner requires
        // extra load time in order to display

        ensureRCEContentIsLoaded().then(function () {
          return printWithCss(_this3.props.persistedPrintFontSize, 300);
        });
      }
      if (prevState && prevState.isOnline !== window.navigator.onLine) {
        this.handleNetworkStatus();
      }
      this.props.makeStyles();
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      if (this.timerService) {
        this.timerService.stop();
      }
      this.props.takingActions.updateIsTaking(false);
      this.clearFetchQuizSessionTimeout();
      window.removeEventListener('online', this.handleNetworkStatus);
      window.removeEventListener('offline', this.handleNetworkStatus);
      window.removeEventListener('focus', this.debouncedHandleWindowFocus);
      window.removeEventListener('blur', this.debouncedHandleWindowBlur);
    }
  }, {
    key: "printOnLoad",
    value: function printOnLoad() {
      return window.location.search.includes('printOnLoad');
    }
  }, {
    key: "takeQuiz",
    value: function takeQuiz() {
      // Reset the delay to start with fast refreshes
      this.sessionRefreshDelay = MIN_REFRESH_DELAY;
      this.makeTakeQuizRequest();
    }
  }, {
    key: "makeTakeQuizRequest",
    value: function makeTakeQuizRequest() {
      var _this$props = this.props,
        onTakeUnauthorized = _this$props.onTakeUnauthorized,
        quizSessionId = _this$props.quizSessionId,
        newQuizTakingSession = _this$props.newQuizTakingSession;
      this.sessionRequestTime = performance.now();
      newQuizTakingSession(quizSessionId, this.waitForQuizSessionToBeReady, onTakeUnauthorized);
    }
  }, {
    key: "clearFetchQuizSessionTimeout",
    value: function clearFetchQuizSessionTimeout() {
      if (!this.fetchQuizSessionTimeout) return;
      clearTimeout(this.fetchQuizSessionTimeout);
      this.fetchQuizSessionTimeout = null;
      this.sessionWaitTime = 0;
    }
  }, {
    key: "onQuizSessionReady",
    value: function onQuizSessionReady() {
      this.props.takingActions.updateIsTaking(true);
      var quizIsTimed = !!this.props.quizSession.endAt;
      if (this.props.useTimer && !this.quizIsFinished()) {
        this.timerService = quizIsTimed ? new TimerService(partial(this.props.getTimeRemaining, this.props.quizSessionId), this.props.takingActions.updateTimerInfo, this.props.getTimerData, this.autoSubmitQuiz, this.props.addInfo, this.props.takingActions.setWSToken) : new TimeRecordingService(partial(this.props.recordQuizTime, this.props.quizSessionId), this.props.takingActions.updateTimerInfo);
        this.timerService.start();
        if (quizIsTimed && this.props.showTimer) {
          this.notifyAboutRemainingTime();
        }
      }
      this.props.onQuizSessionReadyForTaking(this.props.quizSession);
      if (this.props.rceError) {
        this.props.reportRCEEvent(this.props.rceError);
      }
    }
  }, {
    key: "quizIsFinished",
    value: function quizIsFinished() {
      var quizSession = this.props.quizSession;
      if (!quizSession.isLoaded()) {
        return false;
      }

      // in_progress is set async, so we'll also let pending determine the status here
      return !(quizSession.inProgress() || quizSession.pending()) || this.props.timeIsUp;
    }
  }, {
    key: "renderHeader",
    value:
    // =============
    //   RENDERING
    // =============

    function renderHeader() {
      var currentSessionItemPosition = this.props.currentSessionItemPosition;
      var hideInstructions = currentSessionItemPosition !== 1 && this.props.isOneQuestionAtATime; // hides instructions if no longer on first question
      var takeHeader = jsx(TakeHeader, {
        currentSessionItemPosition: currentSessionItemPosition,
        quizSessionId: this.props.quizSessionId,
        hideInstructions: hideInstructions
      });
      if (!this.props.isOneQuestionAtATime || currentSessionItemPosition === 1) {
        return takeHeader;
      }
      return jsx(ScreenReaderContent, null, takeHeader);
    }
  }, {
    key: "renderOverlay",
    value: function renderOverlay() {
      var isOpen = this.quizIsFinished();
      var startedInProgress = this.props.quizSessionStatusOnPageLoad === IN_PROGRESS;
      var quizIsTimed = this.props.quizSession.get('endAt');
      var text, label;
      if (!isOpen) {
        text = '';
        label = '';
      } else if (!quizIsTimed) {
        text = label = t('This quiz has been submitted.');
      } else if (startedInProgress) {
        text = t('Time is up!');
        label = t('Time has expired. Your assessment has been submitted.');
      } else {
        text = label = t('Your allotted time for this quiz has expired.');
      }
      return jsx(Overlay, {
        onEntered: this.focusOnButton,
        open: isOpen,
        shouldContainFocus: true,
        transition: "fade",
        label: label
      }, jsx(Mask, null, jsx("div", {
        css: this.props.styles.overlayBody
      }, jsx("div", {
        css: this.props.styles.overlayItem
      }, jsx("h2", null, text)), jsx("div", {
        css: this.props.styles.overlayItem
      }, jsx(Button, {
        ref: this.overlayBtnRef,
        color: "primary",
        onClick: this.props.onSubmitQuizSession,
        "data-automation": "sdk-go-to-results-button"
      }, t('Go To Results'))))));
    }
  }, {
    key: "renderIsOffline",
    value: function renderIsOffline() {
      var label = t('Connection Lost');
      var text = t('Please refresh the screen and try again. Your progress will be saved.');
      var button = jsx(Button, {
        ref: this.modalBtnRef,
        color: "primary",
        onClick: this.refreshPage,
        "data-automation": "sdk-refresh-page"
      }, t('Refresh'));
      return jsx(SimpleModal, {
        isModalOpen: true,
        onClose: this.refreshPage,
        label: label,
        title: label,
        footerContent: button,
        showCloseButton: false
      }, jsx("div", null, text));
    }
  }, {
    key: "renderFailedToStart",
    value: function renderFailedToStart() {
      return jsx(Alert, {
        liveRegion: liveRegion,
        margin: "large",
        variant: "error"
      }, t('Something went wrong. Please try again.'));
    }
  }, {
    key: "render",
    value: function render() {
      var _this$props2 = this.props,
        currentSessionItemPosition = _this$props2.currentSessionItemPosition,
        onSubmitQuizSession = _this$props2.onSubmitQuizSession,
        quizSession = _this$props2.quizSession,
        quizSessionId = _this$props2.quizSessionId;
      var isOnline = this.state.isOnline;
      if (quizSession.failedToStart()) {
        return this.renderFailedToStart();
      }
      if (!this.readyToTake()) {
        return jsx(Spinner, null);
      }
      return jsx(Page, {
        childrenWrapperRef: this.pageChildrenWrapperRef,
        contentRole: this.props.contentRole,
        sidebarComponent: TakingSidebar,
        sidebarOpen: this.props.sidebarOpen,
        sidebarRole: this.props.sidebarRole,
        toggleSidebar: this.props.toggleSidebar
      }, jsx(Card, {
        wrapped: true,
        css: this.props.styles.root
      }, this.renderOverlay(), isOnline === false ? this.renderIsOffline() : null, this.renderHeader(), jsx("div", {
        "data-automation": "sdk-take-component"
      }, jsx(TakeBody, {
        currentSessionItemPosition: currentSessionItemPosition,
        quizSessionId: quizSessionId,
        lockDown: this.props.lockDown,
        separatorConfig: this.props.separatorConfig
      })), jsx("div", null, jsx(TakeFooter, {
        currentSessionItemPosition: currentSessionItemPosition,
        onSubmitQuizSession: onSubmitQuizSession,
        quizSessionId: quizSessionId,
        returnToUrl: this.props.returnToUrl
      }))));
    }
  }]);
}(Component);
_BaseTake = BaseTake;
_defineProperty(BaseTake, "displayName", 'Take');
_defineProperty(BaseTake, "componentId", "Quizzes".concat(_BaseTake.displayName));
_defineProperty(BaseTake, "propTypes", {
  addInfo: PropTypes.func.isRequired,
  appContainer: CustomPropTypes.selectors.isRequired,
  closeSubmitConfirmationModal: PropTypes.func.isRequired,
  currentSessionItemPosition: PropTypes.number,
  fetchQuizSession: PropTypes.func.isRequired,
  getTimeRemaining: PropTypes.func.isRequired,
  getTimerData: PropTypes.func.isRequired,
  isOneQuestionAtATime: PropTypes.bool.isRequired,
  newQuizTakingSession: PropTypes.func.isRequired,
  onQuizSessionReadyForTaking: PropTypes.func.isRequired,
  onSubmitQuizSession: PropTypes.func.isRequired,
  // consumer provided
  onTakeFailed: PropTypes.func,
  // consumer provided
  onTakeUnauthorized: PropTypes.func,
  // consumer provided
  quizSession: ImmutablePropTypes.record.isRequired,
  quizSessionId: PropTypes.string.isRequired,
  quizSessionStatusOnPageLoad: PropTypes.string.isRequired,
  screenreaderNotification: PropTypes.func.isRequired,
  rceError: PropTypes.string,
  recordQuizTime: PropTypes.func.isRequired,
  returnToUrl: PropTypes.string,
  // consumer provided
  resumeDataLoaded: PropTypes.bool.isRequired,
  submitQuiz: PropTypes.func.isRequired,
  reportRCEEvent: PropTypes.func.isRequired,
  contentRole: PropTypes.string,
  sidebarRole: PropTypes.string,
  toggleSidebar: PropTypes.func,
  sidebarOpen: PropTypes.bool,
  takingActions: PropTypes.shape({
    updateIsTaking: PropTypes.func,
    updateTimerInfo: PropTypes.func,
    setWSToken: PropTypes.func,
    reportPageBlurredEvent: PropTypes.func,
    reportPageFocusedEvent: PropTypes.func
  }).isRequired,
  timeIsUp: PropTypes.bool,
  useTimer: PropTypes.bool,
  showTimer: PropTypes.bool,
  persistedPrintFontSize: PropTypes.string,
  afterQuizSessionItemsRendered: PropTypes.func,
  // consumer provided
  lockDown: PropTypes.bool,
  separatorConfig: PropTypes.shape({
    decimalSeparator: PropTypes.string,
    thousandSeparator: PropTypes.string
  }),
  makeStyles: PropTypes.func,
  styles: PropTypes.object
});
_defineProperty(BaseTake, "defaultProps", {
  contentRole: void 0,
  sidebarRole: void 0,
  toggleSidebar: void 0,
  sidebarOpen: void 0,
  currentSessionItemPosition: 1,
  onSubmitQuizSession: function onSubmitQuizSession() {},
  onTakeFailed: function onTakeFailed() {},
  onTakeUnauthorized: function onTakeUnauthorized() {},
  returnToUrl: null,
  timeIsUp: false,
  useTimer: true,
  showTimer: false,
  afterQuizSessionRendered: null,
  rceError: null,
  afterQuizSessionItemsRendered: Function.prototype,
  lockDown: false,
  persistedPrintFontSize: 'medium',
  hideInstructions: false
});
export var Take = withStyleOverrides(generateStyle, generateComponentTheme)(BaseTake);
export default Take;